import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Switch,
  Chip,
  Tabs,
  Tab,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Collapse,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { DataGrid } from "@mui/x-data-grid";
import { perlColors, perlTypography } from "../../design/tokens";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import { apiFetch } from "../../api/apiClient";

const UserAccessManagementPage = () => {
  const [activeTab, setActiveTab] = useState(0); // 0=Kearney, 1=Client
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, active, inactive
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const snackbarTimerRef = useRef(null);
  const compactFieldSx = {
    width: 300,
    "& .MuiOutlinedInput-root": {
      backgroundColor: perlColors.surface,
      color: perlColors.textPrimary,
      height: 36,
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px",
      fontSize: "13px",
    },
    "& .MuiInputLabel-root": {
      color: perlColors.textSecondary,
      fontSize: "12px",
    },
  };
  const formatLastLogin = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      const day = String(d.getDate()).padStart(2, "0");
      const month = d.toLocaleString("en-US", { month: "short" });
      const year = String(d.getFullYear()).slice(-2);
      const time = d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      return `${day}-${month}-${year} ${time}`;
    } catch {
      return String(value);
    }
  };

  const calcDaysSince = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "-";
      const diffMs = Date.now() - d.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return Number.isFinite(days) ? days : "-";
    } catch {
      return "-";
    }
  };

  const getLastLoginTime = (value) => {
    if (!value) return 0;
    const d = new Date(value);
    const time = d.getTime();
    return Number.isFinite(time) ? time : 0;
  };

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userName: "",
    designation: "",
    role: "",
    userType: "kearney",
    pi: "",
    active: true,
    appAccess: [],
    viewAccess: [],
  });

  // App Access structure
  const appAccessStructure = {
    Mechanical: ["Plastics", "Sheet Metal", "Casting & Machining", "Forging", "Fabrication"],
    Electronics: ["PCB", "Semiconductor", "Actives"],
    Packaging: ["Corrugated", "Laminates", "Bottles", "Foam/Cushion"],
    "F&B": ["Cookies", "Ice Creams", "Carbonated Drinks", "Meat"],
    BPCH: ["Shampoo", "Face Serum", "Perfume", "Body Lotion", "Bar Soap"],
  };

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, [activeTab, filterStatus, searchQuery]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const tabMap = ["kearney", "client"];
      const userType = tabMap[activeTab];

      const params = new URLSearchParams();
      params.append("userType", userType);
      if (filterStatus !== "all") {
        params.append("status", filterStatus);
      }
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      console.log("Fetching users with params:", params.toString());
      const response = await apiFetch(`/api/admin/users?${params.toString()}`);

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error:", errorData);
        throw new Error(errorData.error || "Failed to fetch users");
      }

      const data = await response.json();
      console.log("Fetched users:", data.users);
      setUsers(data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      // alert(`Failed to load users: ${error.message}`);
      // Fallback to empty array on error
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await apiFetch(`/api/admin/users/${userId}/toggle-active`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to toggle user status");
      }

      const data = await response.json();

      // Update local state
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, active: data.active } : user)),
      );
    } catch (error) {
      console.error("Error toggling user status:", error);
      alert("Failed to update user status");
    }
  };

  const getFilteredUsers = () => {
    // Filtering is now done on the backend
    return users;
  };

  const filteredUsers = getFilteredUsers();
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aTime = getLastLoginTime(a?.lastLogin);
    const bTime = getLastLoginTime(b?.lastLogin);
    if (bTime !== aTime) {
      return bTime - aTime;
    }
    return (b?.id || 0) - (a?.id || 0);
  });

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleExpandAllCategories = () => {
    const expanded = Object.keys(appAccessStructure).reduce((acc, category) => {
      acc[category] = true;
      return acc;
    }, {});
    setExpandedCategories(expanded);
  };

  const handleCollapseAllCategories = () => {
    const collapsed = Object.keys(appAccessStructure).reduce((acc, category) => {
      acc[category] = false;
      return acc;
    }, {});
    setExpandedCategories(collapsed);
  };

  const accessCheckboxSx = {
    p: 0,
    color: "rgba(255,255,255,0.45)",
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.2)",
    },
    "&.Mui-checked": {
      color: perlColors.brandColor || perlColors.white,
    },
    "&.MuiCheckbox-indeterminate": {
      color: perlColors.brandColor || perlColors.white,
    },
  };

  const handleAppAccessChange = (category, commodity, checked) => {
    const accessKey = `${category} - ${commodity}`;
    setFormData((prev) => {
      if (!prev.viewAccess.includes(accessKey)) {
        return prev;
      }
      const newAccess = checked
        ? [...prev.appAccess, accessKey]
        : prev.appAccess.filter((item) => item !== accessKey);
      return { ...prev, appAccess: newAccess };
    });
  };

  const handleSelectAllCategory = (category, commodities) => {
    const allSelected = commodities.every((commodity) =>
      formData.appAccess.includes(`${category} - ${commodity}`),
    );

    setFormData((prev) => {
      if (allSelected) {
        // Deselect all
        return {
          ...prev,
          appAccess: prev.appAccess.filter((item) => !item.startsWith(`${category} - `)),
        };
      } else {
        // Select all
        const newItems = commodities.map((commodity) => `${category} - ${commodity}`);
        const existingItems = prev.appAccess.filter((item) => !item.startsWith(`${category} - `));
        return {
          ...prev,
          appAccess: [...existingItems, ...newItems],
        };
      }
    });
  };

  const handleToggleCategoryView = (category, commodities) => {
    const anySelected = commodities.some((commodity) =>
      formData.viewAccess.includes(`${category} - ${commodity}`),
    );

    setFormData((prev) => {
      if (anySelected) {
        // Turn off view for all in category and clear access
        return {
          ...prev,
          viewAccess: prev.viewAccess.filter((item) => !item.startsWith(`${category} - `)),
          appAccess: prev.appAccess.filter((item) => !item.startsWith(`${category} - `)),
        };
      }

      const newItems = commodities.map((commodity) => `${category} - ${commodity}`);
      const existingItems = prev.viewAccess.filter((item) => !item.startsWith(`${category} - `));
      return {
        ...prev,
        viewAccess: [...existingItems, ...newItems],
      };
    });
  };

  const handleViewChange = (category, commodity, checked) => {
    const accessKey = `${category} - ${commodity}`;
    setFormData((prev) => {
      const newViewAccess = checked
        ? [...prev.viewAccess, accessKey]
        : prev.viewAccess.filter((item) => item !== accessKey);
      const newAppAccess = checked
        ? prev.appAccess
        : prev.appAccess.filter((item) => item !== accessKey);
      return {
        ...prev,
        viewAccess: newViewAccess,
        appAccess: newAppAccess,
      };
    });
  };

  // Normalize appAccess data to match the expected format
  const normalizeAppAccess = (appAccessArray) => {
    if (!appAccessArray || !Array.isArray(appAccessArray)) {
      return [];
    }

    const normalized = [];

    appAccessArray.forEach((item) => {
      if (!item) return; // Skip empty items

      const itemStr = String(item).trim();

      // If already in correct format (e.g., "Electronics - PCB"), use it as is
      if (itemStr.includes(" - ")) {
        normalized.push(itemStr);
      } else {
        // Try to find which category this commodity belongs to (case-insensitive)
        let found = false;
        Object.entries(appAccessStructure).forEach(([category, commodities]) => {
          const matchingCommodity = commodities.find(
            (commodity) => commodity.toLowerCase() === itemStr.toLowerCase(),
          );
          if (matchingCommodity) {
            normalized.push(`${category} - ${matchingCommodity}`);
            found = true;
          }
        });
        // If not found in structure, add as is (might be a custom value)
        if (!found) {
          normalized.push(itemStr);
        }
      }
    });

    return normalized;
  };

  const handleEdit = (user) => {
    setIsEditMode(true);
    setEditingUserId(user.id);

    // Normalize appAccess data
    const normalizedAppAccess = normalizeAppAccess(user.appAccess);
    const normalizedViewAccess = normalizeAppAccess(user.viewAccess || user.appAccess);

    // Pre-populate form with user data
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      userName: user.userName || "",
      designation: user.designation || "",
      role: user.role || "",
      userType: user.userType || (activeTab === 0 ? "kearney" : "client"),
      pi: user.pi || "",
      active: user.active !== undefined ? user.active : true,
      appAccess: normalizedAppAccess,
      viewAccess: normalizedViewAccess,
    });

    // Expand categories that have selected commodities
    const expanded = {};
    Object.keys(appAccessStructure).forEach((category) => {
      const commodities = appAccessStructure[category];
      const hasSelected = commodities.some((commodity) =>
        normalizedAppAccess.includes(`${category} - ${commodity}`),
      );
      if (hasSelected) {
        expanded[category] = true;
      }
    });
    setExpandedCategories(expanded);

    setOpenAddDialog(true);
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      userName: "",
      designation: "",
      role: "",
      userType: activeTab === 0 ? "kearney" : "client",
      pi: "",
      active: true,
      appAccess: [],
      viewAccess: [],
    });
    setExpandedCategories({});
    setOpenAddDialog(true);
  };

  const performSave = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.role) {
      alert("Please fill in all required fields: First Name, Last Name, Email, and Role");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    try {
      const url = isEditMode ? `/api/admin/users/${editingUserId}` : "/api/admin/users";
      const method = isEditMode ? "PUT" : "POST";

      // Prepare the payload
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        userName: formData.userName?.trim() || "",
        designation: formData.designation?.trim() || "",
        role: formData.role,
        appAccess: formData.appAccess || [],
        viewAccess: formData.viewAccess || [],
        active: formData.active !== undefined ? formData.active : true,
        userType: formData.userType,
      };

      // Note: PI field is not saved to database as the column doesn't exist
      // Keeping it in the form for display purposes only

      console.log("Saving user:", { isEditMode, payload });

      const response = await apiFetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage =
          responseData.error ||
          responseData.message ||
          (isEditMode ? "Failed to update user" : "Failed to create user");
        throw new Error(errorMessage);
      }

      // Refresh users list
      await fetchUsers();
      setOpenAddDialog(false);
      setIsEditMode(false);
      setEditingUserId(null);

      // Show success message with snackbar
      const successMessage = isEditMode ? "User updated successfully" : "User created successfully";
      setSnackbar({
        open: true,
        message: successMessage,
        severity: "success",
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        designation: "",
        role: "",
        userType: activeTab === 0 ? "kearney" : "client",
        pi: "",
        active: true,
        appAccess: [],
        viewAccess: [],
      });
    } catch (error) {
      console.error("Error saving user:", error);
      alert(
        `Error: ${
          error.message || (isEditMode ? "Failed to update user" : "Failed to create user")
        }`,
      );
    }
  };

  const handleSave = async () => {
    if (isEditMode) {
      setConfirmSaveOpen(true);
      return;
    }
    await performSave();
  };

  const handleConfirmSaveYes = async () => {
    setConfirmSaveOpen(false);
    await performSave();
  };

  const handleConfirmSaveNo = () => {
    setConfirmSaveOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    if (snackbarTimerRef.current) {
      clearTimeout(snackbarTimerRef.current);
      snackbarTimerRef.current = null;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    return () => {
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = null;
      }
    };
  }, []);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        {/* Fixed Header Section - doesn't scroll */}
        <Box sx={{ flexShrink: 0, mb: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                ...perlTypography.labelSmall,
                color: perlColors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Admin
            </Typography>
            <Typography
              variant="h4"
              sx={{
                ...perlTypography.title,
                color: perlColors.textPrimary,
                fontSize: "32px",
                fontWeight: 300,
              }}
            >
              User Management
            </Typography>
          </Box>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: perlColors.border, mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  ...perlTypography.paragraph,
                  color: perlColors.textSecondary,
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 400,
                  "&.Mui-selected": {
                    color: perlColors.textPrimary,
                    fontWeight: 500,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: perlColors.brandColor,
                  height: 2,
                },
              }}
            >
              <Tab label="Kearney" />
              <Tab label="Client" />
            </Tabs>
          </Box>
          {/* Title with count and actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                ...perlTypography.headline,
                color: perlColors.textPrimary,
                fontSize: "20px",
              }}
            >
              {activeTab === 0 ? "Kearney" : "Client"} Users ({filteredUsers.length})
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Search */}
              <TextField
                placeholder="Search Name or Email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  width: 300,
                  "& .MuiInputBase-root": {
                    height: 32,
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "6px 10px",
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: perlColors.surface,
                    borderRadius: "8px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: perlColors.textSecondary, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{
                          padding: "2px",
                          color: perlColors.textSecondary,
                          "&:hover": {
                            color: perlColors.textPrimary,
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* Filter buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={filterStatus === "all" ? "contained" : "outlined"}
                  onClick={() => setFilterStatus("all")}
                  sx={{
                    textTransform: "none",
                    borderColor: perlColors.border,
                    color: filterStatus === "all" ? perlColors.black : perlColors.textPrimary,
                    backgroundColor: filterStatus === "all" ? perlColors.white : "transparent",
                    minHeight: 32,
                    height: 32,
                    padding: "2px 10px",
                    fontSize: "12px",
                    "&:hover": {
                      backgroundColor:
                        filterStatus === "all" ? perlColors.white : "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === "active" ? "contained" : "outlined"}
                  onClick={() => setFilterStatus("active")}
                  sx={{
                    textTransform: "none",
                    borderColor: perlColors.border,
                    color: filterStatus === "active" ? perlColors.black : perlColors.textPrimary,
                    backgroundColor: filterStatus === "active" ? perlColors.white : "transparent",
                    minHeight: 32,
                    height: 32,
                    padding: "2px 10px",
                    fontSize: "12px",
                    "&:hover": {
                      backgroundColor:
                        filterStatus === "active" ? perlColors.white : "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  Active
                </Button>
                <Button
                  variant={filterStatus === "inactive" ? "contained" : "outlined"}
                  onClick={() => setFilterStatus("inactive")}
                  sx={{
                    textTransform: "none",
                    borderColor: perlColors.border,
                    color: filterStatus === "inactive" ? perlColors.black : perlColors.textPrimary,
                    backgroundColor: filterStatus === "inactive" ? perlColors.white : "transparent",
                    minHeight: 32,
                    height: 32,
                    padding: "2px 10px",
                    fontSize: "12px",
                    "&:hover": {
                      backgroundColor:
                        filterStatus === "inactive" ? perlColors.white : "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  Inactive
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Table Section - independent layout */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            mx: -3, // Negative margin to counteract SidebarLayout padding
            px: 3, // Add padding back for content spacing
          }}
        >
          {/* Table */}
          <Paper sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <Typography sx={{ color: perlColors.textSecondary }}>Loading users...</Typography>
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                <DataGrid
                  rows={sortedUsers}
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      minWidth: 100,
                      flex: 1,
                    },
                    {
                      field: "lastName",
                      headerName: "Last Name",
                      minWidth: 100,
                      flex: 1,
                    },
                    {
                      field: "email",
                      headerName: "Email ID",
                      minWidth: 180,
                      flex: 1,
                    },
                    {
                      field: "lastLogin",
                      headerName: "Last Login",
                      minWidth: 170,
                      flex: 1,
                      renderCell: (params) => formatLastLogin(params.value),
                    },
                    {
                      field: "daysSinceLogin",
                      headerName: "Days Since Login",
                      minWidth: 150,
                      flex: 0.8,
                      renderCell: (params) => calcDaysSince(params?.row?.lastLogin),
                    },
                    {
                      field: "loginCount",
                      headerName: "Login Count",
                      minWidth: 120,
                      flex: 0.6,
                      renderCell: (params) =>
                        params.value === null || params.value === undefined ? "-" : params.value,
                    },
                    {
                      field: "userName",
                      headerName: "User Name",
                      minWidth: 100,
                      flex: 1,
                      renderCell: (params) => params.row.userName || "-",
                    },
                    {
                      field: "designation",
                      headerName: "Designation",
                      minWidth: 120,
                      flex: 1,
                    },
                    {
                      field: "role",
                      headerName: "Role",
                      minWidth: 100,
                      flex: 1,
                    },
                    {
                      field: "appAccess",
                      headerName: "App Access",
                      minWidth: 300,
                      flex: 2,
                      renderCell: (params) => (
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
                          {params.value.map((app, idx) => (
                            <Chip
                              key={idx}
                              label={app}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(121, 130, 134, 0.2)",
                                color: perlColors.textPrimary,
                                fontSize: "12px",
                                height: "24px",
                              }}
                            />
                          ))}
                        </Box>
                      ),
                    },
                    {
                      field: "active",
                      headerName: "Active",
                      minWidth: 100,
                      flex: 0.5,
                      renderCell: (params) => (
                        <Switch
                          checked={params.value}
                          onChange={() => handleToggleActive(params.row.id)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: perlColors.white,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: perlColors.white,
                            },
                          }}
                        />
                      ),
                    },
                    {
                      field: "actions",
                      headerName: "Actions",
                      minWidth: 120,
                      flex: 0.5,
                      sortable: false,
                      renderCell: (params) => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            width: "100%",
                          }}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(params.row)}
                              sx={{
                                color: perlColors.textPrimary,
                                border: `1px solid ${perlColors.border}`,
                                "&:hover": {
                                  borderColor: perlColors.textPrimary,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ),
                    },
                  ]}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  rowHeight={52}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${perlColors.border}`,
                      color: perlColors.textPrimary,
                      ...perlTypography.labelSmall,
                      fontSize: "13px",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: perlColors.background,
                      borderBottom: `1px solid ${perlColors.border}`,
                      color: perlColors.textSecondary,
                      ...perlTypography.labelSmall,
                      fontWeight: 500,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 500,
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.02)",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      overflowX: "auto",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                      height: "12px",
                      width: "12px",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                      backgroundColor: "#1a1a1a",
                      borderRadius: "4px",
                    },
                    "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                      backgroundColor: "#555",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "#777",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add User Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: perlColors.background,
            color: perlColors.textPrimary,
            maxWidth: "900px",
            width: "90%",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...perlTypography.headline,
            color: perlColors.textPrimary,
            borderBottom: `1px solid ${perlColors.border}`,
            pb: 2,
          }}
        >
          {isEditMode ? "Edit User" : "Add New User"}
        </DialogTitle>
        <DialogContent
          sx={{
            mt: 2,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: "70vh",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sx={{ mt: 3 }}>
                <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange("firstName", e.target.value)}
                  disabled={isEditMode}
                  size="small"
                  sx={compactFieldSx}
                />
              </Grid>
              <Grid item xs={6} sx={{ mt: 3 }}>
                <TextField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange("lastName", e.target.value)}
                  disabled={isEditMode}
                  size="small"
                  sx={compactFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email ID"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  disabled={isEditMode}
                  size="small"
                  sx={compactFieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="User Name"
                  value={formData.userName}
                  onChange={(e) => handleFormChange("userName", e.target.value)}
                  size="small"
                  sx={compactFieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Designation"
                  value={formData.designation}
                  onChange={(e) => handleFormChange("designation", e.target.value)}
                  size="small"
                  sx={compactFieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl sx={{ width: 300 }} size="small">
                  <InputLabel
                    id="role-select-label"
                    sx={{
                      color: perlColors.textSecondary,
                      fontSize: "12px",
                      "&.Mui-focused": {
                        color: perlColors.textSecondary,
                      },
                    }}
                  >
                    Role
                  </InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={formData.role}
                    onChange={(e) => handleFormChange("role", e.target.value)}
                    label="Role"
                    size="small"
                    inputProps={{
                      style: {
                        width: "100%",
                        minWidth: 0,
                      },
                    }}
                    renderValue={(value) => (
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          display: "block",
                          maxWidth: "100%",
                        }}
                      >
                        {value || ""}
                      </Box>
                    )}
                    sx={{
                      backgroundColor: perlColors.surface,
                      color: perlColors.textPrimary,
                      width: "100%",
                      "& .MuiSelect-select": {
                        width: "100% !important",
                        minWidth: "0 !important",
                        maxWidth: "100% !important",
                        overflow: "hidden !important",
                        textOverflow: "ellipsis !important",
                        whiteSpace: "nowrap !important",
                        boxSizing: "border-box !important",
                        display: "flex !important",
                        alignItems: "center",
                        padding: "8px 32px 8px 10px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-input": {
                        width: "100% !important",
                        minWidth: "0 !important",
                        maxWidth: "100% !important",
                        paddingRight: "32px !important",
                        boxSizing: "border-box !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        width: "100% !important",
                        height: 36,
                        "& fieldset": {
                          borderColor: perlColors.border,
                        },
                        "&:hover fieldset": {
                          borderColor: perlColors.textSecondary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: perlColors.textSecondary,
                        },
                      },
                      "& .MuiSvgIcon-root": {
                        color: perlColors.textSecondary,
                        right: "8px",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: perlColors.surface,
                          color: perlColors.textPrimary,
                          "& .MuiMenuItem-root": {
                            color: perlColors.textPrimary,
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.05)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "rgba(255,255,255,0.1)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.15)",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="Super Admin">Super Admin</MenuItem>
                    <MenuItem value="Admin">Admin</MenuItem>
                    <MenuItem value="SME">SME</MenuItem>
                    <MenuItem value="Ops">Ops</MenuItem>
                    <MenuItem value="End User">End User</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl sx={{ width: 300 }} size="small">
                  <InputLabel
                    id="user-type-select-label"
                    sx={{
                      color: perlColors.textSecondary,
                      fontSize: "12px",
                      "&.Mui-focused": {
                        color: perlColors.textSecondary,
                      },
                    }}
                  >
                    User Type
                  </InputLabel>
                  <Select
                    labelId="user-type-select-label"
                    value={formData.userType}
                    onChange={(e) => handleFormChange("userType", e.target.value)}
                    label="User Type"
                    size="small"
                    sx={{
                      backgroundColor: perlColors.surface,
                      color: perlColors.textPrimary,
                      "& .MuiSelect-select": {
                        padding: "8px 32px 8px 10px",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-root": {
                        height: 36,
                        "& fieldset": {
                          borderColor: perlColors.border,
                        },
                        "&:hover fieldset": {
                          borderColor: perlColors.textSecondary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: perlColors.textSecondary,
                        },
                      },
                      "& .MuiSvgIcon-root": {
                        color: perlColors.textSecondary,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: perlColors.surface,
                          color: perlColors.textPrimary,
                          "& .MuiMenuItem-root": {
                            color: perlColors.textPrimary,
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.05)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "rgba(255,255,255,0.1)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.15)",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="kearney">Kearney</MenuItem>
                    <MenuItem value="client">Client</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {/* <TextField
              label="PI"
              value={formData.pi}
              onChange={(e) => handleFormChange("pi", e.target.value)}
              sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
              backgroundColor: perlColors.surface,
              color: perlColors.textPrimary,
              },
              "& .MuiInputLabel-root": {
              color: perlColors.textSecondary,
              },
              }}
              /> */}
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => handleFormChange("active", e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: perlColors.white,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: perlColors.white,
                        },
                      }}
                    />
                  }
                  label="Active"
                  sx={{ color: perlColors.textPrimary }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  ...perlTypography.labelSmall,
                  color: perlColors.textSecondary,
                }}
              >
                App Access
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Expand All">
                  <IconButton
                    size="small"
                    onClick={handleExpandAllCategories}
                    sx={{
                      color: perlColors.textPrimary,
                      border: `1px solid ${perlColors.border}`,
                      "&:hover": {
                        borderColor: perlColors.textPrimary,
                        backgroundColor: "rgba(255,255,255,0.05)",
                      },
                    }}
                  >
                    <UnfoldMoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Collapse All">
                  <IconButton
                    size="small"
                    onClick={handleCollapseAllCategories}
                    sx={{
                      color: perlColors.textPrimary,
                      border: `1px solid ${perlColors.border}`,
                      "&:hover": {
                        borderColor: perlColors.textPrimary,
                        backgroundColor: "rgba(255,255,255,0.05)",
                      },
                    }}
                  >
                    <UnfoldLessIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: perlColors.surface,
                border: `1px solid ${perlColors.border}`,
                borderRadius: "8px",
                flex: 1,
                overflow: "hidden",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                "--view-col": "72px",
                "--access-col": "84px",
                "--expand-col": "28px",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(0, 1fr) var(--view-col) var(--access-col) var(--expand-col)",
                  alignItems: "center",
                  columnGap: 1,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: perlColors.surface,
                  borderBottom: `1px solid ${perlColors.border}`,
                }}
              >
                <Typography
                  sx={{
                    ...perlTypography.labelSmall,
                    color: perlColors.textSecondary,
                    fontSize: "12px",
                  }}
                >
                  Category / Commodity
                </Typography>
                <Typography
                  sx={{
                    ...perlTypography.labelSmall,
                    color: perlColors.textSecondary,
                    fontSize: "12px",
                  }}
                >
                  View
                </Typography>
                <Typography
                  sx={{
                    ...perlTypography.labelSmall,
                    color: perlColors.textSecondary,
                    fontSize: "12px",
                  }}
                >
                  Access
                </Typography>
                <Box />
              </Box>
              <Box sx={{ flex: 1, overflow: "auto", p: 1.5, pt: 1 }}>
                {Object.entries(appAccessStructure).map(([category, commodities]) => {
                  const viewAllSelected = commodities.every((commodity) =>
                    formData.viewAccess.includes(`${category} - ${commodity}`),
                  );
                  const viewSomeSelected = commodities.some((commodity) =>
                    formData.viewAccess.includes(`${category} - ${commodity}`),
                  );
                  const viewEnabledCommodities = commodities.filter((commodity) =>
                    formData.viewAccess.includes(`${category} - ${commodity}`),
                  );
                  const accessAnySelected = viewEnabledCommodities.some((commodity) =>
                    formData.appAccess.includes(`${category} - ${commodity}`),
                  );

                  return (
                    <Box key={category} sx={{ mb: 0.5 }}>
                      {/* Category Header */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(0, 1fr) var(--view-col) var(--access-col) var(--expand-col)",
                          alignItems: "center",
                          columnGap: 1,
                          width: "100%",
                          p: 1,
                          backgroundColor: accessAnySelected
                            ? "rgba(255,255,255,0.08)"
                            : expandedCategories[category]
                              ? "rgba(255,255,255,0.05)"
                              : "transparent",
                          borderRadius: "4px",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.05)",
                          },
                        }}
                        onClick={() => handleToggleCategory(category)}
                      >
                        <Typography
                          sx={{
                            ...perlTypography.paragraph,
                            color: perlColors.textPrimary,
                            fontWeight: 500,
                          }}
                        >
                          {category}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Checkbox
                            size="small"
                            checked={viewSomeSelected}
                            indeterminate={!viewAllSelected && viewSomeSelected}
                            onChange={() => handleToggleCategoryView(category, commodities)}
                            onClick={(e) => e.stopPropagation()}
                            sx={accessCheckboxSx}
                          />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Checkbox
                            size="small"
                            checked={accessAnySelected}
                            onChange={() =>
                              handleSelectAllCategory(category, viewEnabledCommodities)
                            }
                            onClick={(e) => e.stopPropagation()}
                            sx={accessCheckboxSx}
                            disabled={viewEnabledCommodities.length === 0}
                          />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          {expandedCategories[category] ? (
                            <ExpandLessIcon sx={{ color: perlColors.textSecondary }} />
                          ) : (
                            <ExpandMoreIcon sx={{ color: perlColors.textSecondary }} />
                          )}
                        </Box>
                      </Box>
                      {/* Sub-commodities */}
                      <Collapse in={expandedCategories[category]}>
                        <Box sx={{ pt: 0.5 }}>
                          {commodities.map((commodity) => {
                            const isViewEnabled = formData.viewAccess.includes(
                              `${category} - ${commodity}`,
                            );
                            const isSelected = formData.appAccess.includes(
                              `${category} - ${commodity}`,
                            );
                            return (
                              <Box
                                key={commodity}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "minmax(0, 1fr) var(--view-col) var(--access-col) var(--expand-col)",
                                  alignItems: "center",
                                  columnGap: 1,
                                  mb: 0.25,
                                  width: "100%",
                                  px: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    ...perlTypography.paragraph,
                                    color: perlColors.textPrimary,
                                    fontSize: "14px",
                                    pl: 3,
                                  }}
                                >
                                  {commodity}
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <Checkbox
                                    size="small"
                                    checked={isViewEnabled}
                                    onChange={(e) =>
                                      handleViewChange(category, commodity, e.target.checked)
                                    }
                                    sx={accessCheckboxSx}
                                  />
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={(e) =>
                                      handleAppAccessChange(category, commodity, e.target.checked)
                                    }
                                    sx={accessCheckboxSx}
                                    disabled={!isViewEnabled}
                                  />
                                </Box>
                                <Box />
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${perlColors.border}`,
            p: 2,
          }}
        >
          <Button
            onClick={() => {
              setOpenAddDialog(false);
              setIsEditMode(false);
              setEditingUserId(null);
            }}
            sx={{
              textTransform: "none",
              color: perlColors.white,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: perlColors.white,
              color: perlColors.black,
              "&:hover": {
                backgroundColor: perlColors.white,
              },
            }}
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmSaveOpen}
        onClose={handleConfirmSaveNo}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: perlColors.background,
            color: perlColors.textPrimary,
          },
        }}
      >
        <DialogTitle sx={{ color: perlColors.textPrimary }}>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: perlColors.textSecondary }}>
            Save changes to this user?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmSaveNo}
            sx={{ textTransform: "none", color: perlColors.white }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmSaveYes}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: perlColors.white,
              color: perlColors.black,
              "&:hover": {
                backgroundColor: perlColors.white,
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default UserAccessManagementPage;
